// Ported from handover: window.tasklet.runTool → fetch('/api/bookings')
// All UI logic identical to original

import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { PortalBooking } from '../types';
import { formatPortalDate, calcNights, todayISO } from '../utils/helpers';
import { API_BASE } from '../config';

async function fetchFutureBookings(): Promise<PortalBooking[]> {
  let allItems: PortalBooking[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const res = await fetch(`${API_BASE}/api/bookings?page=${page}`);
    if (!res.ok) throw new Error(`API error ${res.status}: ${await res.text()}`);

    const result = await res.json() as {
      success: boolean;
      data: { paging: { current: number; total: number }; items: PortalBooking[] };
    };

    if (!result?.data?.items) throw new Error('Unexpected bookings response shape');

    allItems = allItems.concat(result.data.items);
    totalPages = Math.ceil(result.data.paging.total / 50);
    page++;
  } while (page <= totalPages && allItems.length < 200);

  return allItems.sort((a, b) => a.arrival_date.localeCompare(b.arrival_date));
}

export const BookingsTab: React.FC = () => {
  const [bookings, setBookings] = useState<PortalBooking[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setBookings(await fetchFutureBookings());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const thisMonth = bookings.filter(b => {
    const d   = new Date(b.arrival_date);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const missingFlights = bookings.filter(b => b.lead_guest && b.lead_guest.includes('-'));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading loading-spinner loading-lg text-primary"></span>
        <span className="ml-3 text-base-content/60">Loading bookings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error m-4">
        <AlertCircle size={16} />
        <span>Failed to load bookings: {error}</span>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="stat bg-base-200 rounded-box p-3">
          <div className="stat-title text-xs">Upcoming Bookings</div>
          <div className="stat-value text-2xl text-primary">{bookings.length}</div>
        </div>
        <div className="stat bg-base-200 rounded-box p-3">
          <div className="stat-title text-xs">This Month</div>
          <div className="stat-value text-2xl text-secondary">{thisMonth.length}</div>
        </div>
        <div className="stat bg-base-200 rounded-box p-3">
          <div className="stat-title text-xs">Missing Flights</div>
          <div className={`stat-value text-2xl ${missingFlights.length > 0 ? 'text-warning' : 'text-success'}`}>
            —
          </div>
        </div>
      </div>

      {/* Refresh */}
      <div className="flex justify-end">
        <button className="btn btn-sm btn-ghost gap-1" onClick={load}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-box border border-base-200">
        <table className="table table-sm table-zebra w-full">
          <thead>
            <tr>
              <th>Guest</th>
              <th>Arrival</th>
              <th>Departure</th>
              <th className="text-center">Nights</th>
              <th className="text-center">Adults</th>
              <th className="text-center">Children</th>
              <th>Reference</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map(b => {
              const nights = calcNights(b.arrival_date, b.departure_date);
              const hasRef = b.lead_guest && b.lead_guest.includes('-');
              return (
                <tr key={b.booking_id}>
                  <td className="font-medium">
                    {hasRef ? (
                      <span>
                        {b.lead_guest.split('-').slice(0, 2)
                          .map(p => p.charAt(0).toUpperCase() + p.slice(1))
                          .join(' ')}
                      </span>
                    ) : (
                      <span>{b.lead_guest}</span>
                    )}
                  </td>
                  <td>{formatPortalDate(b.arrival_date)}</td>
                  <td>{formatPortalDate(b.departure_date)}</td>
                  <td className="text-center">{nights}</td>
                  <td className="text-center">{b.adults}</td>
                  <td className="text-center">{b.children || 0}</td>
                  <td className="text-xs text-base-content/50">{b.lead_guest}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
